<template>
	<div
		:class="{
			'accordion--open' : isOpen,
			'accordion--calculate': isCalculatingHeight,
			'accordion--animating': isAnimating,
		}"
		class="accordion"
		data-cy="accordion"
	>
		<div
			:id="id"
			:class="[
				'accordion__trigger',
				`accordion__trigger--${iconPosition}`
			]"
			tabindex="0"
			role="presentation"
			:aria-expanded="isOpen"
			@click="toggleState"
		>
			<helio-icon
				class="accordion__icon"
				:class="[
					'accordion__icon',
					{
						'accordion__icon--rotate': iconRotation,
					}
				]"
				weight="light"
				:token="currentIcon"
			/>

			<template v-if="hasTitleSlot">
				<slot name="title" />
			</template>

			<template v-else>
				<label :for="id">{{ title }}</label>
			</template>

			<helio-icon
				v-if="hint"
				:data-anchor="hint ? `hint_${_uid}` : ''"
				class="accordion__hint"
				token="question-circle"
				weight="bold"
				@mouseover.native="showHint = true"
				@mouseleave.native="showHint = false"
			/>
		</div>

		<helio-tooltip
			v-if="hint"
			:active="showHint"
			:anchor="`hint_${_uid}`"
			position="top"
		>
			<template
				v-slot:content
				:aria-hidden="!isOpen"
			>
				{{ hint }}
			</template>
		</helio-tooltip>

		<div
			ref="content"
			class="accordion__content-wrapper"
		>
			<div class="accordion__content">
				<slot
					v-if="renderWhenClosed || isOpen"
					name="content"
				/>
			</div>
		</div>
	</div>
</template>

<script>
/* eslint-disable no-underscore-dangle */

import Icon from '../icon/Icon.vue';
import Tooltip from '../tooltip/Tooltip.vue';

export default {
	name: 'Accordion',

	components: {
		'helio-icon': Icon,
		'helio-tooltip': Tooltip,
	},

	props: {
		closedIcon: {
			type: String,
			required: false,
			default: 'angle-down',
			description: 'Sets which icon will be used to indicate a closed Accordion',
		},

		hint: {
			type: String,
			required: false,
			default: null,
			description: 'Adds a hint icon (<i class="fas fa-question-circle"></i>). Hovering on the icon opens a tooltip containing the provided string.',
		},

		iconPosition: {
			type: String,
			required: false,
			default: 'left',
			validator: value => [
				'left',
				'right',
			].indexOf(value) !== -1,
			description: 'Sets the location of the icon in the Accordion',
		},

		iconRotation: {
			type: Boolean,
			required: false,
			default: true,
			description: 'Sets whether the icon will rotate when the Accordion is toggled open and closed.',
		},

		open: {
			type: Boolean,
			required: false,
			default: false,
			description: 'Sets whether or not the Accordion will be mounted in its expanded state',
		},

		openedIcon: {
			type: String,
			required: false,
			default: 'angle-down',
			description: 'Sets which icon will be used to indicate an open Accordion',
		},

		renderWhenClosed: {
			type: Boolean,
			required: false,
			default: true,
			description: 'Sets whether or not the content slot will be rendered when the accordion is closed (useful when there is a lot of nested content)',
		},

		title: {
			type: String,
			required: false,
			default: null,
			description: 'Supplies the title of the Accordion&mdash;it will appear if no title slot has been provided',
		},
	},

	slots: {
		content: {
			required: true,
			scoped: false,
			description: 'Supplies the content that will appear when an Accordion is open',
		},

		title: {
			required: false,
			scoped: false,
			description: 'Supplies the title of an Accordion',
		},
	},

	data() {
		return {
			isOpen: false,
			isCalculatingHeight: false,
			isAnimating: false,
			contentHeight: 0,
			animationTime: 0.2,
			showHint: false,
			id: null,
		};
	},

	computed: {
		currentIcon() {
			return this.isOpen ? this.openedIcon : this.closedIcon;
		},

		hasTitleSlot() {
			return !!this.$slots.title || !!this.$scopedSlots.title;
		},
	},

	watch: {
		open(status) {
			if (status) {
				this.animateOpen();
			} else {
				this.animateClose();
			}
		},
	},

	created() {
		this.isOpen = this.open;
	},

	mounted() {
		this.setMutationObserver();
		this.id = this._uid;

		window.addEventListener('resize', this.onResize);
		this.onResize();
	},

	beforeDestroy() {
		window.removeEventListener('resize', this.onResize);
	},

	methods: {
		onResize() {
			if (this.isOpen) {
				const { content } = this.$refs;

				if (content) {
					this.isCalculatingHeight = true;
					content.style.height = 'auto';

					this.$nextTick(() => {
						this.contentHeight = content.clientHeight;
						this.isCalculatingHeight = false;
						content.style.height = `${this.contentHeight}px`;
					});
				}
			}
		},

		toggleState() {
			if (!this.isOpen) {
				this.calculateHeight();
				this.$emit('open');
			} else {
				this.animateClose();
				this.$emit('close');
			}
		},

		calculateHeight() {
			const { content } = this.$refs;

			this.isCalculatingHeight = true;
			this.$nextTick(() => {
				this.contentHeight = content.clientHeight;
				this.isCalculatingHeight = false;
				this.animateOpen();

				if (this.animationTime <= 0.7) {
					const secPerHeight = 0.001;
					const minRange = 0.2;
					const maxRange = 0.7;
					this.animationTime = this.contentHeight * secPerHeight;

					this.animationTime = Math.min(this.animationTime, maxRange).toFixed(2);
					this.animationTime = Math.max(this.animationTime, minRange).toFixed(2);
					document.documentElement.style.setProperty('--time', `${this.animationTime}s`);
				}
			});
		},

		animateOpen() {
			setTimeout(() => {
				const { content } = this.$refs;

				this.isOpen = true;
				content.style.height = `${this.contentHeight}px`;

				setTimeout(() => {
					content.removeAttribute('style');
				}, this.animationTime * 1000);
			}, 0);
		},

		animateClose() {
			const { content } = this.$refs;
			this.isOpen = false;
			content.style.height = `${this.contentHeight}px`;
			setTimeout(() => {
				content.removeAttribute('style');
			}, 0);
		},

		setMutationObserver() {
			if (window.MutationObserver) {
				const { content } = this.$refs;

				// Create the observer and resize the accordion on changes
				// eslint-disable-next-line
				const onObserve = function () {
					this.onResize();
				}.bind(this);

				this.observer = new window.MutationObserver(onObserve);

				// Setup the observer
				this.observer.observe(
					content,
					{
						attributes: false,
						childList: true,
						characterData: true,
						subtree: true,
					},
				);
			}
		},
	},
};
</script>

<style lang="scss" scoped src="./accordion.scss" />
